import React, { useEffect, useRef, useState } from "react";
import { Search } from "lucide-react";
import { useTranslation } from "react-i18next";
import useAutoResizeTextarea from "../../../shared/hooks/useAutoResizeTextarea";
import SearchModeIcon from "./SearchModeIcon";

const formatCreditLabel = ({ count, t }) => {
  if (!Number.isFinite(count) || count < 1) {
    return "";
  }
  if (count === 1) {
    return t("searchHistory.creditSingular", { count });
  }
  return t("searchHistory.creditPlural", { count });
};

export default function SearchInputDock({
  containerRef,
  showSidebar,
  isMobile,
  isHistoryView,
  hasGlobalSearched,
  isSearching,
  query,
  searchMode,
  onSearchModeChange,
  onQueryChange,
  onQueryKeyDown,
  onSubmit,
  searchSubmitNudgeActive,
  searchModeSettings,
  placement = "fixed",
  placeholder,
  showDisclaimer = true,
}) {
  const { t } = useTranslation();
  const searchInputRef = useRef(null);
  const modeMenuRef = useRef(null);
  const [isModeMenuOpen, setIsModeMenuOpen] = useState(false);

  useAutoResizeTextarea(searchInputRef, query);
  const showModeSwitch = !hasGlobalSearched && !isHistoryView;
  const resolveModeDetail = (modeId) => {
    const description = t(`app.searchModes.${modeId}Description`);
    const creditCost = Number(searchModeSettings?.[modeId]?.credit_cost);
    const creditLabel = formatCreditLabel({ count: creditCost, t });
    if (!creditLabel) {
      return description;
    }
    return t("app.searchModes.detailTemplate", {
      credits: creditLabel,
      description,
    });
  };
  const searchModes = [
    {
      id: "basic",
      label: t("app.searchModes.basic"),
      detail: resolveModeDetail("basic"),
    },
    {
      id: "pro",
      label: t("app.searchModes.pro"),
      detail: resolveModeDetail("pro"),
    },
  ];
  const selectedMode = searchModes.find((mode) => mode.id === searchMode) || searchModes[0];
  const currentPlaceholder = placeholder || t("app.placeholder");
  const canSubmit = Boolean(query.trim()) && !isSearching;
  const isInline = placement === "inline";

  useEffect(() => {
    if (!isModeMenuOpen) {
      return undefined;
    }

    const handlePointerDown = (event) => {
      if (!modeMenuRef.current?.contains(event.target)) {
        setIsModeMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
    };
  }, [isModeMenuOpen]);

  return (
    <div
      ref={containerRef}
      className={
        isInline
          ? "relative z-10 flex justify-center pointer-events-none"
          : `fixed bottom-4 z-50 ${showSidebar && !isMobile ? "left-64 right-0" : "left-0 right-0"} flex justify-center pointer-events-none`
      }
    >
      <div className={`${isInline ? "w-full max-w-5xl px-0" : "w-full max-w-3xl px-4"} pointer-events-auto`}>
        <div className="flex flex-col gap-2">
          <div
            className={`search-dock-shell ${isInline ? "search-dock-shell--inline" : ""} relative flex w-full items-center ${
              isSearching ? "bg-gray-50 opacity-75" : ""
            }`}
          >
            {isInline && (
              <Search
                aria-hidden="true"
                className="mr-5 h-7 w-7 flex-shrink-0 text-[#4f46e5]"
                strokeWidth={2.4}
              />
            )}
            <div className="search-dock-input-wrap relative min-w-0 flex-1">
              {!query.trim() && (
                <div className="search-dock-placeholder pointer-events-none absolute inset-y-0 left-0 right-0 overflow-hidden text-ellipsis whitespace-nowrap">
                  {currentPlaceholder}
                </div>
              )}
              <textarea
                ref={searchInputRef}
                rows={1}
                value={query}
                onChange={(event) => onQueryChange(event.target.value)}
                onKeyDown={onQueryKeyDown}
                placeholder={currentPlaceholder}
                className="search-dock-textarea flex-1 w-full placeholder-transparent"
                disabled={isSearching}
              />
            </div>
            <div
              ref={showModeSwitch ? modeMenuRef : null}
              className="search-dock-controls relative ml-2 flex shrink-0 items-center"
              data-testid={showModeSwitch ? "search-mode-switch" : undefined}
            >
              {showModeSwitch && (
                <button
                  type="button"
                  disabled={isSearching}
                  onClick={() => setIsModeMenuOpen((open) => !open)}
                  className={`search-dock-mode-btn ${selectedMode.id === "pro" ? "search-dock-mode-btn-active" : ""} ${
                    isSearching ? "cursor-not-allowed opacity-60" : ""
                  }`}
                >
                  <span className="flex min-w-0 items-center">
                    <SearchModeIcon
                      modeId={selectedMode.id}
                      className="mr-2 h-4 w-4 flex-shrink-0"
                    />
                    <span className="truncate">{selectedMode.label}</span>
                  </span>
                  <svg
                    className={`ml-2 h-4 w-4 flex-shrink-0 transition-transform ${isModeMenuOpen ? "rotate-180" : ""}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="m6 9 6 6 6-6"
                    />
                  </svg>
                </button>
              )}

              <button
                onClick={onSubmit}
                disabled={!query.trim() || isSearching}
                data-testid="search-submit-button"
                className={`search-dock-submit ${!canSubmit ? "search-dock-submit-disabled" : ""} ${
                  searchSubmitNudgeActive && query.trim() && !isSearching
                    ? "search-submit-nudge"
                    : ""
                }`}
              >
                <span>{t("app.search")}</span>
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2.5}
                    d="M5 12h14M12 5l7 7-7 7"
                  />
                </svg>
              </button>

              {showModeSwitch && isModeMenuOpen && (
                <div
                  className={`search-dock-menu absolute right-0 z-20 w-72 overflow-hidden ${
                    isInline ? "top-full mt-3" : "bottom-full mb-3"
                  }`}
                >
                  {searchModes.map((mode) => {
                    const isSelected = mode.id === selectedMode.id;
                    return (
                      <button
                        key={mode.id}
                        type="button"
                        onClick={() => {
                          onSearchModeChange?.(mode.id);
                          setIsModeMenuOpen(false);
                        }}
                        className={`search-dock-menu-item ${isSelected ? "search-dock-menu-item-selected" : ""}`}
                      >
                        <span className="flex min-w-0 items-start">
                          <SearchModeIcon
                            modeId={mode.id}
                            className="mr-3 mt-0.5 h-4 w-4 flex-shrink-0"
                          />
                          <span className="min-w-0">
                            <span className="search-dock-menu-title block leading-none">
                              {mode.label}
                            </span>
                            <span
                              className="search-dock-menu-detail mt-1 block truncate"
                              title={mode.detail}
                            >
                              {mode.detail}
                            </span>
                          </span>
                        </span>
                        {isSelected && (
                          <svg
                            className="ml-3 h-5 w-5 flex-shrink-0 text-blue-600"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2.5}
                              d="m5 13 4 4L19 7"
                            />
                          </svg>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {showDisclaimer && (
            <p className="mt-2 text-[11px] text-slate-400 text-center">
              {t("dashboard.table.disclaimer")}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
